// Custom hook for budget management
import { useState, useEffect } from 'react';
import { budgetService } from '../../backend/services/budget';
import { budgetMonitorService } from '../../backend/services/budgetMonitor';
import { useAuth } from './useAuth';
import type { 
  BudgetCategory, 
  CampaignBudget, 
  BudgetSummary,
  BudgetSnapshot,
  BudgetAlert
} from '../../shared/types/budget';

export const useBudget = (campaignId?: string) => {
  const { user } = useAuth();
  const [categories, setCategories] = useState<BudgetCategory[]>([]);
  const [budgets, setBudgets] = useState<CampaignBudget[]>([]);
  const [summary, setSummary] = useState<BudgetSummary | null>(null);
  const [snapshots, setSnapshots] = useState<BudgetSnapshot[]>([]);
  const [alerts, setAlerts] = useState<BudgetAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [globalStats, setGlobalStats] = useState<{ activeCampaigns: number } | null>(null);

  // Fetch budget categories
  const fetchCategories = async () => {
    try {
      const data = await budgetService.getBudgetCategories();
      setCategories(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch categories');
    }
  };

  // Fetch campaign budgets
  const fetchCampaignBudgets = async (id: string) => {
    try {
      const [budgetsData, summaryData, snapshotsData] = await Promise.all([
        budgetService.getCampaignBudgets(id),
        budgetService.getCampaignBudgetSummary(id),
        budgetService.getBudgetSnapshots(id)
      ]);
      
      setBudgets(budgetsData);
      setSummary(summaryData);
      setSnapshots(snapshotsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch campaign budgets');
    }
  };

  const fetchGlobalSummary = async () => {
    try {
      const { summary: globalSummary, active_campaigns } = await budgetService.getGlobalBudgetSummary();
      setSummary(globalSummary);
      setGlobalStats({ activeCampaigns: active_campaigns });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch global budget summary');
    }
  };

  const fetchGlobalSnapshots = async () => {
    try {
      const snapshotData = await budgetService.getRecentBudgetSnapshots(8);
      setSnapshots(snapshotData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch budget history');
    }
  };

  const fetchAlerts = async (id?: string) => {
    try {
      const alertsData = await budgetMonitorService.getUnacknowledgedAlerts(id);
      const normalized = (alertsData as any[]).map(alert => ({
        id: alert.id,
        campaign_id: alert.campaign_id,
        type: alert.type,
        severity: alert.severity,
        title: alert.title,
        message: alert.message,
        suggested_action: alert.suggested_action,
        created_at: alert.triggered_at || alert.created_at,
        acknowledged: alert.acknowledged,
        campaign: alert.campaigns
          ? {
              id: alert.campaigns.id,
              name: alert.campaigns.name,
              artist: alert.campaigns.artists
                ? {
                    id: alert.campaigns.artists.id,
                    name: alert.campaigns.artists.name
                  }
                : undefined
            }
          : undefined
      })) as BudgetAlert[];
      setAlerts(normalized);
      setSummary(prev => prev ? { ...prev, alerts_count: normalized.length } : prev);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch budget alerts');
    }
  };

  // Create budget item
  const createBudget = async (budget: Omit<CampaignBudget, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      setError(null);
      const newBudget = await budgetService.createCampaignBudget(budget);
      setBudgets(prev => [...prev, newBudget]);
      
      // Refresh summary
      if (campaignId) {
        const newSummary = await budgetService.getCampaignBudgetSummary(campaignId);
        setSummary(newSummary);
      }
      
      return newBudget;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create budget');
      throw err;
    }
  };

  // Update budget item
  const updateBudget = async (id: string, updates: Partial<CampaignBudget>) => {
    try {
      setError(null);
      const updatedBudget = await budgetService.updateCampaignBudget(id, updates);
      setBudgets(prev => prev.map(b => b.id === id ? updatedBudget : b));
      
      // Refresh summary
      if (campaignId) {
        const newSummary = await budgetService.getCampaignBudgetSummary(campaignId);
        setSummary(newSummary);
      }
      
      return updatedBudget;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update budget');
      throw err;
    }
  };

  // Delete budget item
  const deleteBudget = async (id: string) => {
    try {
      setError(null);
      await budgetService.deleteCampaignBudget(id);
      setBudgets(prev => prev.filter(b => b.id !== id));
      
      // Refresh summary
      if (campaignId) {
        const newSummary = await budgetService.getCampaignBudgetSummary(campaignId);
        setSummary(newSummary);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete budget');
      throw err;
    }
  };

  // Create budget snapshot
  const createSnapshot = async () => {
    if (!campaignId) return;
    
    try {
      setError(null);
      const snapshot = await budgetService.createBudgetSnapshot(campaignId, user?.id);
      setSnapshots(prev => [snapshot, ...prev]);
      return snapshot;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create snapshot');
      throw err;
    }
  };

  // Initialize data
  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        await fetchCategories();
        if (campaignId) {
          await fetchCampaignBudgets(campaignId);
          await fetchAlerts(campaignId);
        }
        if (!campaignId) {
          await fetchGlobalSummary();
          await fetchGlobalSnapshots();
          await fetchAlerts();
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize budget data');
      } finally {
        setLoading(false);
      }
    };

    initializeData();
  }, [campaignId]);

  return {
    // Data
    categories,
    budgets,
    summary,
    snapshots,
    alerts,
    loading,
    error,
    globalStats,

    // Actions
    createBudget,
    updateBudget,
    deleteBudget,
    createSnapshot,
    
    // Refresh functions
    refetchCategories: fetchCategories,
    refetchBudgets: campaignId ? () => fetchCampaignBudgets(campaignId) : undefined
  };
};

// Hook for budget categories management
export const useBudgetCategories = () => {
  const [categories, setCategories] = useState<BudgetCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await budgetService.getBudgetCategories();
      setCategories(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async (category: Omit<BudgetCategory, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      setError(null);
      const newCategory = await budgetService.createBudgetCategory(category);
      setCategories(prev => [...prev, newCategory]);
      return newCategory;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create category');
      throw err;
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return {
    categories,
    loading,
    error,
    createCategory,
    refetch: fetchCategories
  };
};
